import React, { useEffect, useMemo, useRef, useState } from 'react';

import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { FORM_ERROR } from 'final-form';
import { SITE_URL } from 'components/SeoHead/SeoHead';
import {
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  ChartColumn,
  Megaphone,
  Package,
  SearchCheck,
  TicketPercent,
  Users,
  Wallet,
} from 'lucide-react';
import { ChevronDown, MapPin, Search as SearchIcon, Sliders, X as CloseIcon } from 'react-feather';
import { injectIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Feedback, Spinner, SeoHead } from 'components';
import { actions as blogActions } from 'coupon-common/src/modules/admin/blogManager';
import { actions as emailActions } from 'coupon-common/src/modules/email';
import { dispatchAsAsync } from 'coupon-common/src/utils';
import { convertYoutubeLinksToIframes } from 'containers/AdminPanel/BlogManager/BlogManager';
import MyCouponLogo from '../../assets/images/MyCouponLogo.svg';
import { FAQSection } from 'containers/LandingPage/components/Page1/FAQSection';

import { LocationSearchModal } from '../Coupons/LocationSearch';
import styles from './WebsiteBlogs.module.scss';

// ─── Static category tiles for the "Browse by category" section ──────────
// These aren't part of the blog data model — they're fixed nav tiles, same
// as the reference design. `matchKeywords` is used to count/filter blogs
// whose tags loosely match a category (case-insensitive substring match).
const BLOG_CATEGORIES = [
  {
    key: 'news-updates',
    icon: Megaphone,
    title: 'News & Updates',
    description:
      'The latest features, fresh partnerships, and platform updates designed to help you experience more from your city.',
    matchKeywords: ['news', 'update', 'announcement', 'partnership', 'giveaway'],
  },
  {
    key: 'budgeting-savings',
    icon: Wallet,
    title: 'Budgeting & Savings',
    description:
      'Practical strategies and everyday money-saving tactics focused on maximizing your budget, so you can do more without spending more.',
    matchKeywords: ['budget', 'saving', 'money', 'freebie', 'reward'],
  },
  {
    key: 'app-insights',
    icon: ChartColumn,
    title: 'App Insights',
    description:
      'Your insider guide to mastering the platform, discovering hidden features, and getting the most out of your Premium Membership.',
    matchKeywords: ['app insight', 'using the app', 'app tips', 'app feature', 'premium', 'membership'],
  },
  {
    key: 'product-highlights',
    icon: Package,
    title: 'Product Highlights',
    description:
      'Deep dives into specific local deals and items, giving you the exact details you need to make confident, rewarding purchases.',
    matchKeywords: ['product', 'review', 'beauty', 'skincare'],
  },
  {
    key: 'top-finds',
    icon: SearchCheck,
    title: 'Top Finds',
    description:
      'Curated roundups of the best active offers and things to do, tapping into your city so that you can experience more.',
    matchKeywords: ['top finds', 'things to do', 'best', 'guide', 'roundup'],
  },
  {
    key: 'business-spotlight',
    icon: BriefcaseBusiness,
    title: 'Business Spotlight',
    description:
      'Inspiring stories behind the local businesses that make your community unique, highlighting how your support helps them thrive while you earn rewards.',
    matchKeywords: ['business', 'spotlight', 'merchant', 'local business'],
  },
];

// Category tiles for the Business Resources page — same layout and matching
// rules as BLOG_CATEGORIES above, with business-facing topics.
const BUSINESS_RESOURCE_CATEGORIES = [
  {
    key: 'news-updates',
    icon: Megaphone,
    title: 'News & Updates',
    description:
      'The latest features, fresh partnerships, and platform updates designed to help your business get more from My Couponbook.',
    matchKeywords: ['news', 'update', 'announcement', 'partnership'],
  },
  {
    key: 'marketing-insights',
    icon: ChartColumn,
    title: 'Marketing Insights',
    description:
      'Practical marketing ideas and local trends to help you reach more customers without spending more on ads.',
    matchKeywords: ['marketing', 'insight', 'advertising', 'social media', 'business tools'],
  },
  {
    key: 'guides-tutorials',
    icon: BookOpen,
    title: 'Guides & Tutorials',
    description:
      'Step-by-step walkthroughs for setting up your profile, publishing deals, and getting the most out of your dashboard.',
    matchKeywords: ['guide', 'tutorial', 'how to', 'walkthrough', 'tips'],
  },
  {
    key: 'coupon-strategies',
    icon: TicketPercent,
    title: 'Coupon Strategies',
    description:
      'Proven deal ideas and coupon tactics that turn first-time visitors into regulars while protecting your margins.',
    matchKeywords: ['coupon', 'deal', 'discount', 'offer', 'promo'],
  },
  {
    key: 'community-support',
    icon: Users,
    title: 'Community & Support',
    description:
      'Stories from fellow local businesses, answers to common questions, and help whenever you need a hand.',
    matchKeywords: ['community', 'support', 'spotlight', 'local business', 'help'],
  },
  {
    key: 'events',
    icon: CalendarDays,
    title: 'Events',
    description:
      'Upcoming workshops, webinars, and local happenings where you can learn, network, and grow your business.',
    matchKeywords: ['event', 'workshop', 'webinar', 'festival', 'meetup'],
  },
];

// The Blogs page and the Business Resources page share this screen; each
// variant supplies its own URL base, category tiles and SEO copy.
const VARIANTS = {
  blogs: {
    basePath: '/blogs',
    categories: BLOG_CATEGORIES,
    seoTitle: 'Blogs & News',
    seoDescription: 'Read the latest savings tips, coupon news, and shopping guides from My Couponbook.',
    contentPillar: 'b2c',
    heading: 'Local Blogs',
    showBusinessResourcesButton: true,
  },
  businessResources: {
    basePath: '/business-resources',
    categories: BUSINESS_RESOURCE_CATEGORIES,
    seoTitle: 'Business Resources',
    seoDescription:
      'Marketing insights, guides, coupon strategies, and news to help local businesses grow with My Couponbook.',
    contentPillar: 'b2b',
    heading: 'Business Resources',
    showBusinessResourcesButton: false,
  },
};

const PAGE_SIZE = 6;
const SUPPORT_EMAIL = 'support@mycouponbook.ca';
const DEFAULT_CITY_NAME = 'Halifax';
const HERO_IMAGE = '/images/Webp/CoffeeShopBG.webp';

// ─── Contact Support modal ───────────────────────────────────────────────
// Public-facing copy (this page is for everyone, not just business owners).
// Subject `value`s are the same ones the account-page modal sends, so the
// backend sees identical values; only the labels are reworded.
const CONTACT_RED = '#C0392B';

const CONTACT_SUBJECTS = [
  { value: 'general', label: 'General Question' },
  { value: 'technical', label: 'Using the App' },
  { value: 'coupons', label: 'Coupons & Rewards' },
  { value: 'billing', label: 'Account & Billing' },
];

const contactLabelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6,
};

const contactInputStyle = {
  width: '100%', boxSizing: 'border-box',
  border: '1.5px solid #e5e7eb', borderRadius: 8,
  padding: '10px 12px', fontSize: 13, color: '#111',
  outline: 'none', background: '#fff', fontFamily: 'inherit',
  transition: 'border-color .15s',
};

const focusRed = e => { e.target.style.borderColor = CONTACT_RED; };
const blurGray = e => { e.target.style.borderColor = '#e5e7eb'; };

const ContactModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(CONTACT_SUBJECTS[0].value);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleBackdrop = e => { if (e.target === e.currentTarget) onClose(); };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting) return;
    if (!name.trim() || !email.trim() || !message.trim()) return;

    setSubmitError(null);
    setSubmitting(true);

    try {
      const result = await dispatchAsAsync(
        emailActions.sendContactMessage({
          name: name.trim(),
          email: email.trim(),
          subject,
          message: message.trim(),
        }),
        dispatch,
      );

      // Same convention as updateInfo: resolves with nothing on success,
      // with an error payload otherwise.
      if (!result) {
        setSent(true);
      } else if (result[FORM_ERROR]) {
        setSubmitError(result[FORM_ERROR]);
      } else {
        const text = Object.entries(result)
          .map(([field, msg]) => `${field}: ${Array.isArray(msg) ? msg.join(', ') : msg}`)
          .join(' ');
        setSubmitError(text || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[WebsiteBlogs] ContactModal submit — threw:', err);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(17,24,39,0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 20,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Contact support"
        style={{
          position: 'relative',
          width: '100%', maxWidth: 860, maxHeight: '90vh', overflowY: 'auto',
          borderRadius: 24, background: '#fff',
          boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
          display: 'flex', flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 20, right: 20, zIndex: 1,
            width: 34, height: 34, borderRadius: '50%',
            border: '1.5px solid #e5e7eb', background: '#fff',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="#555" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>

        {/* Left — pitch + email */}
        <div style={{
          flex: '1 1 320px', padding: '48px 44px',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}
        >
          <img
            src={MyCouponLogo}
            alt="MyCouponBook"
            style={{ alignSelf: 'flex-start', height: 64, width: 'auto', marginBottom: 20 }}
          />
          <div style={{
            fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
            color: CONTACT_RED, textTransform: 'uppercase', marginBottom: 16,
          }}
          >
            Support
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a1a', lineHeight: 1.25, margin: '0 0 14px' }}>
            How can we <span style={{ color: CONTACT_RED }}>help</span>?
          </h2>
          <p style={{ fontSize: 14, color: '#777', lineHeight: 1.6, margin: '0 0 28px' }}>
            Questions about the app, your rewards, or a coupon? Send us a message and our
            support team will get back to you. We&apos;re here 7 days a week.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              width: 36, height: 36, borderRadius: '50%', background: '#fbe4e1',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke={CONTACT_RED} strokeWidth="1.3" />
                <path d="M1.5 4l6.5 4.5L14.5 4" stroke={CONTACT_RED} strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            </span>
            <div>
              <div style={{ fontSize: 11.5, color: '#999' }}>Prefer email?</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>support@mycouponbook.ca</div>
            </div>
          </div>
        </div>

        {/* Right — form card */}
        <div style={{
          flex: '1 1 360px', background: '#f7f4f4',
          padding: 28, display: 'flex', alignItems: 'center',
        }}
        >
          <div style={{
            width: '100%', background: '#fff', borderRadius: 20,
            padding: 28, boxShadow: '0 16px 40px rgba(0,0,0,0.08)',
          }}
          >
            {sent ? (
              <div style={{ textAlign: 'center', padding: '24px 4px' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a', marginBottom: 8 }}>
                  Message sent
                </div>
                <p style={{ fontSize: 13, color: '#777', margin: '0 0 18px' }}>
                  Thanks for reaching out. We&apos;ll reply to the email address you provided.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    border: 'none', borderRadius: 999, padding: '11px 28px',
                    background: CONTACT_RED, color: '#fff',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="cb-contact-name" style={contactLabelStyle}>Name</label>
                  <input
                    id="cb-contact-name"
                    type="text"
                    required
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your name"
                    style={contactInputStyle}
                    onFocus={focusRed}
                    onBlur={blurGray}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="cb-contact-email" style={contactLabelStyle}>Email</label>
                  <input
                    id="cb-contact-email"
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    style={contactInputStyle}
                    onFocus={focusRed}
                    onBlur={blurGray}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="cb-contact-subject" style={contactLabelStyle}>What&apos;s this about?</label>
                  <select
                    id="cb-contact-subject"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    style={{ ...contactInputStyle, cursor: 'pointer' }}
                    onFocus={focusRed}
                    onBlur={blurGray}
                  >
                    {CONTACT_SUBJECTS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="cb-contact-message" style={contactLabelStyle}>Message</label>
                  <textarea
                    id="cb-contact-message"
                    required
                    rows={4}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Tell us how we can help"
                    style={{ ...contactInputStyle, resize: 'vertical' }}
                    onFocus={focusRed}
                    onBlur={blurGray}
                  />
                </div>

                {submitError && (
                  <div style={{
                    background: '#fff2f2', border: '1px solid #fca5a5',
                    borderRadius: 8, padding: '10px 14px',
                    fontSize: 13, color: CONTACT_RED, marginTop: 14,
                  }}
                  >
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    marginTop: 18, width: '100%',
                    border: 'none', borderRadius: 999, padding: '15px 22px',
                    background: 'linear-gradient(145deg, #e03030 0%, #b91c1c 100%)',
                    color: '#fff', fontSize: 14, fontWeight: 700,
                    cursor: submitting ? 'default' : 'pointer',
                    opacity: submitting ? 0.7 : 1,
                    fontFamily: 'inherit',
                    boxShadow: '0 8px 20px rgba(192,57,43,0.35)',
                  }}
                >
                  {submitting ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const isPublished = blog => {
  if (!blog) return false;

  const status = String(blog.status || '').toLowerCase();

  if (status) {
    return status === 'published' || status === 'public' || status === 'live';
  }

  // Fall back to explicit boolean flags if the API doesn't send a status.
  if (typeof blog.is_published === 'boolean') return blog.is_published;
  if (typeof blog.published === 'boolean') return blog.published;
  if (typeof blog.is_draft === 'boolean') return !blog.is_draft;

  return true;
};

const stripHtml = html => {
  if (!html) return '';

  const div = document.createElement('div');
  div.innerHTML = DOMPurify.sanitize(String(html));

  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
};

const getTagList = tags => {
  if (!tags) return [];
  return tags.split(',').map(tag => tag.trim()).filter(Boolean);
};

const WebsiteBlogs = ({ intl, blogs, blogDetail, variant }) => {
  const {
    basePath,
    categories,
    contentPillar,
    heading,
    seoTitle: pageSeoTitle,
    seoDescription: pageSeoDescription,
    showBusinessResourcesButton,
  } = VARIANTS[variant] || VARIANTS.blogs;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { blogSlug: slug } = useParams();
  const gridRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [activeBlog, setActiveBlog] = useState(null);
  const [activeAuthor, setActiveAuthor] = useState(null);
  const [contactModal, setContactModal] = useState(false);
  const [supportEmailCopied, setSupportEmailCopied] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  const activeCityName = useSelector(state => (state.cities && state.cities.activeCity) || DEFAULT_CITY_NAME);
  const activeCityId = useSelector(state => state.cities && state.cities.activeCityId);

  // mailto: silently does nothing on desktops without a default mail app,
  // so also copy the address and confirm it on screen.
  const handleContactSupport = () => {
    const confirmCopied = () => setSupportEmailCopied(true);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(SUPPORT_EMAIL).then(confirmCopied, confirmCopied);
    } else {
      confirmCopied();
    }
  };

  // Refetch whenever the page variant (b2c/b2b) or the chosen city changes.
  // Blogs with no city, or tied to a business, are returned for every city.
  useEffect(() => {
    dispatch(blogActions.getBlogs({
      content_pillar: contentPillar,
      ...(activeCityId ? { location_city: activeCityId } : {}),
    }));
  }, [dispatch, contentPillar, activeCityId]);

  useEffect(() => {
    if (slug) {
      dispatch(blogActions.getBlogBySlug(slug));
    }
  }, [dispatch, slug]);

  const getArrayData = data => {
    if (Array.isArray(data)) {
      return data;
    }

    if (data && Array.isArray(data.results)) {
      return data.results;
    }

    return [];
  };

  // Drafts (and anything not explicitly published) never reach the public page.
  const allBlogs = getArrayData(blogs && blogs.data).filter(isPublished);

//   console.log('[WebsiteBlogs] allBlogs slugs:', allBlogs.map(b => ({
  
//   id: b.id,
//   title: b.title,
//   slug: b.slug,
//   authorDetails: b.author_details,
//   authorSlug: b.author_details && b.author_details.slug,
// })));

  const rawDetailBlog = blogDetail && blogDetail.data;
  const detailBlog = isPublished(rawDetailBlog) ? rawDetailBlog : null;

  // Support direct links to /blogs/:authorSlug/:blogSlug by auto-opening the
  // modal once we have data for that blog, either from the already-fetched
  // list or from the single-blog detail call.
  useEffect(() => {
    if (!slug) return;

    const fromList = allBlogs.find(blog => String(blog.slug) === String(slug));

    if (fromList) {
      setActiveBlog(fromList);
    } else if (detailBlog && String(detailBlog.slug) === String(slug)) {
      setActiveBlog(detailBlog);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, blogs, blogDetail]);

  // Lock body scroll while any modal is open.
  useEffect(() => {
    if (activeBlog || activeAuthor || contactModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [activeBlog, activeAuthor, contactModal]);

  const closeBlog = () => {
    setActiveBlog(null);
    navigate(basePath);
  };

  // Esc closes whichever modal is on top. (The contact modal handles its
  // own Escape key, so it's skipped here to avoid also closing a blog.)
  useEffect(() => {
    const onKeyDown = event => {
      if (event.key !== 'Escape') return;
      if (contactModal) return;

      if (activeAuthor) {
        setActiveAuthor(null);
      } else if (activeBlog) {
        closeBlog();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBlog, activeAuthor, contactModal]);

  const formatDate = date => {
    if (!date) return 'Recently published';

    return new Date(date).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Card meta date — "Mar 3", no year.
  const formatShortDate = date => {
    if (!date) return 'Recently';

    return new Date(date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  const getPreviewText = (text, length = 220) => {
    if (!text) return '';

    return text.length > length ? `${text.slice(0, length)}...` : text;
  };

  const renderTags = tags => {
    if (!tags) return null;

    return getTagList(tags).map(tag => (
      <span key={tag} styleName="tag-pill">
        {tag}
      </span>
    ));
  };

  const renderLinks = links => {
    if (!links) return null;

    return links.split(',').map(link => link.trim()).filter(Boolean).map(link => (
      <a
        key={link}
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        styleName="blog-link"
        onClick={event => event.stopPropagation()}
      >
        Visit link
      </a>
    ));
  };

  const openBlog = blog => {
    if (!blog) return;

    setActiveBlog(blog);

    const authorSlug = blog.author_details && blog.author_details.slug;

    if (authorSlug && blog.slug) {
      navigate(`${basePath}/${authorSlug}/${blog.slug}`);
    }
  };

  const openAuthor = (author, event) => {
    if (event) event.stopPropagation();
    if (!author) return;

    setActiveAuthor(author);
  };

  const closeAuthor = () => setActiveAuthor(null);

  const renderAuthor = (author, compact) => {
    const authorLink = author.website_url || author.linkedin_url;
    return (
      <div
        styleName={compact ? 'author-row author-row-compact' : 'author-row'}
        onClick={event => openAuthor(author, event)}
        role="button"
        tabIndex={0}
      >
        {author.profile_image ? (
          <img
            src={author.profile_image}
            alt={author.display_name}
            styleName={compact ? 'profile-picture profile-picture-sm' : 'profile-picture'}
          />
        ) : (
          <div styleName={compact ? 'profile-placeholder profile-placeholder-sm' : 'profile-placeholder'}>
            {(author.display_name || 'A').charAt(0)}
          </div>
        )}

        <div>
          <div styleName="author-name-row">
            <p styleName={compact ? 'author-name author-name-sm' : 'author-name'}>
              {author.display_name ? `By ${author.display_name}` : 'By Author'}
            </p>

            {authorLink && (
              <a
                href={authorLink}
                target="_blank"
                rel="noopener noreferrer"
                styleName="author-link-icon"
                onClick={event => event.stopPropagation()}
              >
                ↗
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Category helpers ──────────────────────────────────────────────────
  const categoryMatchesBlog = (category, blog) => {
    const haystack = getTagList(blog.tags).join(' ').toLowerCase();
    if (!haystack) return false;
    return category.matchKeywords.some(keyword => haystack.includes(keyword));
  };

  // Badge label: the first "Browse by category" tile this blog falls under,
  // falling back to its first raw tag when it matches none.
  const getBlogCategoryLabel = blog => {
    const category = categories.find(item => categoryMatchesBlog(item, blog));
    return category ? category.title : getTagList(blog.tags)[0];
  };

  // Up to two outlined pills stacked on a blog image: its category first,
  // then its remaining tags.
  const getBlogBadges = blog => {
    const labels = [getBlogCategoryLabel(blog), ...getTagList(blog.tags)].filter(Boolean);
    const seen = new Set();

    return labels
      .filter(label => {
        const key = label.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 2);
  };

  const renderBadges = (blog, large) => {
    const badges = getBlogBadges(blog);
    if (!badges.length) return null;

    return (
      <div styleName={large ? 'badge-stack badge-stack-lg' : 'badge-stack'}>
        {badges.map(label => (
          <span key={label} styleName="category-badge">
            {label}
          </span>
        ))}
      </div>
    );
  };

  const categoryCounts = useMemo(() => {
    const counts = {};
    categories.forEach(category => {
      counts[category.key] = allBlogs.filter(blog => categoryMatchesBlog(category, blog)).length;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBlogs]);

  const scrollToGrid = () => {
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCategoryClick = category => {
    setSelectedCategory(prev => (prev === category.key ? null : category.key));
    setVisibleCount(PAGE_SIZE);
    scrollToGrid();
  };

  const clearCategoryFilter = () => {
    setSelectedCategory(null);
    setVisibleCount(PAGE_SIZE);
  };

  // ─── Search + category filtering ───────────────────────────────────────
  const term = searchTerm.trim().toLowerCase();
  const isSearching = term.length > 0;

  const matchesSearch = blog => {
    const haystack = [blog.title, blog.tags, stripHtml(blog.body)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(term);
  };

  const activeCategory = categories.find(category => category.key === selectedCategory) || null;

  const filteredBlogs = useMemo(() => {
    let list = allBlogs;

    if (activeCategory) {
      list = list.filter(blog => categoryMatchesBlog(activeCategory, blog));
    }

    if (isSearching) {
      list = list.filter(matchesSearch);
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBlogs, activeCategory, term]);

  const latestBlog = allBlogs[0];
  const recentBlogs = allBlogs.slice(1, 5);

  const visibleBlogs = filteredBlogs.slice(0, visibleCount);
  const hasMore = filteredBlogs.length > visibleBlogs.length;

  const similarBlogs = useMemo(() => {
    if (!activeBlog) return [];
    return allBlogs.filter(blog => String(blog.id) !== String(activeBlog.id)).slice(0, 3);
  }, [activeBlog, allBlogs]);

  // ─── Card renderers ─────────────────────────────────────────────────────
  const renderBlogCard = blog => {
    const author = blog.author_details || {};

    return (
      <article key={blog.id} styleName="blog-card" onClick={() => openBlog(blog)}>
        <div styleName="blog-card-image-wrap">
          {blog.hero_image ? (
            <img src={blog.hero_image} alt={blog.title} styleName="blog-card-image" />
          ) : (
            <div styleName="hero-placeholder">Blog</div>
          )}

          {renderBadges(blog)}
        </div>

        <div styleName="blog-card-body">
          <h3 styleName="blog-card-title">{blog.title}</h3>

          <div styleName="blog-card-meta">
            {renderAuthor(author, true)}
            <span styleName="meta-dot">.</span>
            <span>{formatShortDate(blog.publish_datetime)}</span>
            <span styleName="meta-dot">.</span>
            <span>{blog.reading_time || 0} min read</span>
          </div>
        </div>
      </article>
    );
  };

  const renderRecentItem = blog => (
    <div key={blog.id} styleName="recent-item" onClick={() => openBlog(blog)}>
      <div styleName="recent-thumb-wrap">
        {blog.hero_image ? (
          <img src={blog.hero_image} alt={blog.title} styleName="recent-thumb" />
        ) : (
          <div styleName="recent-thumb-placeholder">Blog</div>
        )}
      </div>

      <p styleName="recent-text">{getPreviewText(blog.title || stripHtml(blog.body), 90)}</p>
    </div>
  );


  
    // ─── SEO ────────────────────────────────────────────────────────────
// Reuses this file's own openBlog() logic for the URL shape
// (/blogs/:authorSlug/:blogSlug) so canonical/og:url match exactly what
// gets pushed to the address bar when a blog is opened.
const activeBlogAuthorSlug = activeBlog && activeBlog.author_details && activeBlog.author_details.slug;
const blogPath =
  activeBlog && activeBlogAuthorSlug && activeBlog.slug ? `${basePath}/${activeBlogAuthorSlug}/${activeBlog.slug}` : basePath;

const seoTitle = activeBlog ? activeBlog.meta_title || activeBlog.title : pageSeoTitle;
const seoDescription = activeBlog
  ? activeBlog.meta_description || getPreviewText(stripHtml(activeBlog.body), 160)
  : pageSeoDescription;

const structuredData = activeBlog
  ? {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: activeBlog.title,
      description: seoDescription,
      image: activeBlog.hero_image || undefined,
      datePublished: activeBlog.publish_datetime || undefined,
      author:
        activeBlog.author_details && activeBlog.author_details.display_name
          ? { '@type': 'Person', name: activeBlog.author_details.display_name }
          : undefined,
      url: `${SITE_URL}${blogPath}`,
    }
  : undefined;


  const renderBlogModal = () => {
    if (!activeBlog) return null;

    const author = activeBlog.author_details || {};
    const primaryTag = getTagList(activeBlog.tags)[0];

    const convertedHtml = convertYoutubeLinksToIframes(
      String(activeBlog.body || '').replace(/&nbsp;/g, ' '),
    );

    const bodyHtml = DOMPurify.sanitize(convertedHtml, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: [
        'target',
        'rel',
        'src',
        'allowfullscreen',
        'frameborder',
        'class',
      ],
    });

    return (
      <div styleName="modal-overlay modal-overlay-article" onClick={closeBlog}>
        <div styleName="modal-stack" onClick={event => event.stopPropagation()}>
          {/* Floats directly on the dark backdrop, above the white card —
              not attached to the card and not a "sticky" element that
              pins while the card scrolls underneath it. */}
          <div styleName="modal-floating-bar">
            {renderAuthor(author, true)}

            <button
              type="button"
              styleName="modal-close"
              onClick={closeBlog}
              aria-label="Close"
            >
              <CloseIcon size={18} />
            </button>
          </div>

          <div styleName="modal-card">
            <div styleName="modal-body">
              <div styleName="modal-meta-row">
                <span styleName="modal-date">{formatDate(activeBlog.publish_datetime)}</span>
                {primaryTag && <span styleName="modal-category-badge">{primaryTag}</span>}
              </div>

              <h1 styleName="modal-title">{activeBlog.title}</h1>

              <div styleName="modal-hero-wrap">
                {activeBlog.hero_image ? (
                  <img src={activeBlog.hero_image} alt={activeBlog.title} styleName="modal-hero-image" />
                ) : (
                  <div styleName="hero-placeholder">Blog</div>
                )}
              </div>

              <div
                styleName="modal-full-body"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />

              <div styleName="bottom-row">
                <div styleName="tags-row">{renderTags(activeBlog.tags)}</div>
                <div styleName="links-row">{renderLinks(activeBlog.links)}</div>
              </div>
            </div>
          </div>

          {/* Sits outside/below the white card, directly on the dark
              backdrop — not part of the card itself. */}
          {similarBlogs.length > 0 && (
            <div styleName="similar-section-outside">
              <h4 styleName="similar-heading">Similar Blogs</h4>

              <div styleName="similar-grid">
                {similarBlogs.map(blog => {
                  const similarAuthor = blog.author_details || {};
                  const similarTag = getTagList(blog.tags)[0];

                  return (
                    <article key={blog.id} styleName="similar-card" onClick={() => openBlog(blog)}>
                      <div styleName="similar-image-wrap">
                        {blog.hero_image ? (
                          <img src={blog.hero_image} alt={blog.title} styleName="similar-image" />
                        ) : (
                          <div styleName="hero-placeholder">Blog</div>
                        )}
                        {similarTag && (
                          <div styleName="badge-stack">
                            <span styleName="category-badge">{similarTag}</span>
                          </div>
                        )}
                      </div>

                      <p styleName="similar-title">{blog.title}</p>
                      <p styleName="similar-author">
                        {(similarAuthor.display_name && `By ${similarAuthor.display_name}`) || 'By Author'}
                      </p>
                    </article>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAuthorModal = () => {
    if (!activeAuthor) return null;

    const author = activeAuthor;
    const authorLink = author.website_url || author.linkedin_url;

    return (
      <div styleName="modal-overlay" onClick={closeAuthor}>
        <div styleName="author-modal-card" onClick={event => event.stopPropagation()}>
          <button
            type="button"
            styleName="modal-close"
            onClick={closeAuthor}
            aria-label="Close"
          >
            <CloseIcon size={18} />
          </button>

          {author.profile_image ? (
            <img
              src={author.profile_image}
              alt={author.display_name}
              styleName="author-modal-picture"
            />
          ) : (
            <div styleName="author-modal-placeholder">
              {(author.display_name || 'A').charAt(0)}
            </div>
          )}

          <h3 styleName="author-modal-name">{author.display_name || 'Author'}</h3>

          <p styleName="author-modal-bio">
            {author.bio || 'This author has not added a bio yet.'}
          </p>

          {authorLink && (
            <a
              href={authorLink}
              target="_blank"
              rel="noopener noreferrer"
              styleName="blog-link"
            >
              Visit profile
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div styleName="page-wrap">
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        image={activeBlog ? activeBlog.hero_image : undefined}
        path={blogPath}
        type={activeBlog ? 'article' : 'website'}
        structuredData={structuredData}
      />

      {/* ─── Hero — full-bleed photo, CustomerHeader overlays it ─── */}
      <section styleName="hero" style={{ backgroundImage: `url(${HERO_IMAGE})` }}>
        <div styleName="page-inner hero-inner">
          <h1 styleName="page-heading">
            Read. Discover.
            <br />
            <span styleName="page-heading-accent">Stay Updated.</span>
          </h1>

          <p styleName="hero-subtitle">
            Local stories, business insights, and helpful tips{' '}
            <br />
            all in one place.
          </p>

          {showBusinessResourcesButton && (
            <button
              type="button"
              styleName="hero-btn"
              onClick={() => navigate(VARIANTS.businessResources.basePath)}
            >
              Business Resources
            </button>
          )}
        </div>
      </section>

      <div styleName="page-inner">
        {/* ─── Local Blogs: latest blog + recent list ─── */}
        <section styleName="page-header">
          <div styleName="hero-top-row">
            <div styleName="local-heading-wrap">
              <h2 styleName="local-heading">
                <svg styleName="local-heading-pin" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7 12 7.41 12.41a.83.83 0 0 0 1.18 0C13 22 20 15.25 20 10c0-4.42-3.58-8-8-8Zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z" />
                </svg>
                {heading}
              </h2>
              <p styleName="local-subheading">
                Showing blogs from {activeCityId ? activeCityName : 'all cities'}
              </p>
            </div>

            <button type="button" styleName="location-pill" onClick={() => setIsLocationModalOpen(true)}>
              <MapPin size={15} />
              <span styleName="location-label">Select Location</span>
            </button>
          </div>

          <div styleName="page-eyebrow">
            <span styleName="eyebrow-dot" />
            Blogs &amp; News
          </div>

          {!blogs.fetching && latestBlog && (
            <div styleName="hero-content-row">
              <article styleName="latest-card" onClick={() => openBlog(latestBlog)}>
                {latestBlog.hero_image ? (
                  <img src={latestBlog.hero_image} alt={latestBlog.title} styleName="latest-image" />
                ) : (
                  <div styleName="hero-placeholder">Blog</div>
                )}

                {renderBadges(latestBlog, true)}

                <div styleName="latest-overlay">
                  <h3 styleName="latest-label">Latest blog</h3>
                  <p styleName="latest-preview">
                    {getPreviewText(stripHtml(latestBlog.body) || latestBlog.title, 220)}
                  </p>
                </div>
              </article>

              {recentBlogs.length > 0 && (
                <div styleName="recent-panel">
                  <div styleName="recent-list">{recentBlogs.map(renderRecentItem)}</div>

                  <button type="button" styleName="see-all-link" onClick={scrollToGrid}>
                    See all
                    <svg width="34" height="12" viewBox="0 0 34 12" fill="none" aria-hidden="true">
                      <path d="M0 6h32M27 1l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        {blogs.fetching ? (
          <Spinner show />
        ) : (
          <>
            {/* ─── Browse by category ─── */}
            <section styleName="category-section">
              <div styleName="category-eyebrow">
                <span styleName="eyebrow-dot" />
                Browse by category
              </div>

              <div styleName="category-grid">
                {categories.map(category => {
                  const Icon = category.icon;
                  const count = categoryCounts[category.key] || 0;
                  const active = selectedCategory === category.key;

                  return (
                    <button
                      type="button"
                      key={category.key}
                      styleName={active ? 'category-card category-card-active' : 'category-card'}
                      onClick={() => handleCategoryClick(category)}
                    >
                      <span styleName="category-icon">
                        <Icon size={20} strokeWidth={1.5} />
                      </span>
                      <span styleName="category-title">{category.title}</span>
                      <span styleName="category-description">{category.description}</span>
                      <span styleName="category-count">{count > 0 ? `${count} article${count === 1 ? '' : 's'}` : 'Coming soon'}</span>
                    </button>
                  );
                })}
              </div>
            </section>

          
            {/* ─── Search + filter ─── */}
            <div ref={gridRef} styleName="toolbar">
              <button
                type="button"
                styleName={isFilterOpen ? 'filter-btn filter-btn-active' : 'filter-btn'}
                onClick={() => setIsFilterOpen(prev => !prev)}
              >
                <Sliders size={16} />
                <span styleName="filter-label">Filter</span>
              </button>

              <div styleName="search-box">
                <SearchIcon size={18} styleName="search-icon" />
                <input
                  type="text"
                  placeholder="Search for blog.."
                  value={searchTerm}
                  onChange={event => {
                    setSearchTerm(event.target.value);
                    setVisibleCount(PAGE_SIZE);
                  }}
                  styleName="search-input"
                />
              </div>
            </div>

            {isFilterOpen && (
              <div styleName="filter-pills-row">
                {categories.map(category => (
                  <button
                    type="button"
                    key={category.key}
                    styleName={selectedCategory === category.key ? 'filter-pill filter-pill-active' : 'filter-pill'}
                    onClick={() => handleCategoryClick(category)}
                  >
                    {category.title}
                  </button>
                ))}
                {selectedCategory && (
                  <button type="button" styleName="filter-pill filter-pill-clear" onClick={clearCategoryFilter}>
                    Clear filter ✕
                  </button>
                )}
              </div>
            )}

            {/* ─── Blog grid ─── */}
            {visibleBlogs.length > 0 ? (
              <div styleName="blog-grid">{visibleBlogs.map(renderBlogCard)}</div>
            ) : (
              <div styleName="empty-state">
                <h4>No blogs found</h4>
                <p>
                  {isSearching || activeCategory
                    ? 'Try a different search term or category.'
                    : 'More blogs will appear here soon.'}
                </p>
              </div>
            )}

            {hasMore && (
              <div styleName="view-more-row">
                <button
                  type="button"
                  styleName="view-more-btn"
                  onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
                >
                  View More
                  <ChevronDown size={18} />
                </button>
              </div>
            )}
          </>
        )}

        {/* ─── FAQ (same as landing page) ─── */}
        <div styleName="faq-section">
          <FAQSection containerClassName="w-full" />
        </div>

                <div styleName="contact-card">
          <h4>Still have questions?</h4>
          <p>Our support team is here to help you 7 days a week.</p>
          <button type="button" styleName="contact-button" onClick={() => setContactModal(true)}>
            Contact Support
          </button>
        </div>

        <Feedback
          show={!!((blogs && blogs.error) || (blogDetail && blogDetail.error))}
          invalid
          content={(blogs && blogs.error) || (blogDetail && blogDetail.error)}
        />
      </div>

      {renderBlogModal()}
      {renderAuthorModal()}
      {contactModal && <ContactModal onClose={() => setContactModal(false)} />}
      <LocationSearchModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
      />
     
    </div>
  );
};

WebsiteBlogs.propTypes = {
  intl: PropTypes.object,
  blogs: PropTypes.object,
  blogDetail: PropTypes.object,
  variant: PropTypes.oneOf(Object.keys(VARIANTS)),
};

const mapStateToProps = state => ({
  blogs: state.admin.blogManager.list,
  blogDetail: state.admin.blogManager.detail,
});

export default connect(mapStateToProps)(
  injectIntl(CSSModule(WebsiteBlogs, styles, { allowMultiple: true })),
);